import React, { memo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import { Button, Divider, Flex, Text } from 'pcln-design-system'
import { ChevronDown, User } from 'pcln-icons'
import getByPath from '../../utils/getByPath'
import { Menu, MenuItem } from 'pcln-menu'

const CenteredText = styled(Text)`
  display: flex;
  align-items: center;
  white-space: nowrap;
`

const HoverText = styled(Text.span)`
  &:hover {
    text-decoration: underline;
  }
`

const MenuFlex = styled(Flex)`
  &&& div {
    display: flex;
  }
`

const SignedInButton = styled(Button)`
  &:hover {
    text-decoration: none;
  }
`

const SmallMenuItem = styled(MenuItem)`
  &&& {
    padding-left: ${themeGet('space.1')};
  }
`

const StyledDivider = styled(Divider)`
  margin-top: ${themeGet('space.3')};
`

function SignedIn({ color, profile = {}, pointsName, membershipName, onLogoutClicked }) {
  const { fields = {}, data = {} } = profile

  const points = getByPath(data, fields.points) ?? ''
  const membershipNumber = getByPath(data, fields.memberId) ?? ''
  const firstName = getByPath(data, fields.firstName) ?? ''
  const buttonNode = (
    <SignedInButton color={color} size='small' variation='link'>
      <CenteredText color={color} alignItems='center'>
        <User color={color} mr={2} />
        <HoverText>{firstName ? `Hi, ${firstName}` : 'My Profile'}</HoverText>
        <ChevronDown />
        {points !== '' && `| ${points} ${pointsName}`}
      </CenteredText>
    </SignedInButton>
  )

  return (
    <MenuFlex alignItems='center'>
      <Menu
        id='signed-in-menu'
        ariaLabel='Click to view account or access sign out option'
        buttonNode={buttonNode}
        trapFocus={false}
        width={300}
      >
        <Content
          points={points}
          pointsName={pointsName}
          membershipName={membershipName}
          membershipNumber={membershipNumber}
          onLogoutClicked={onLogoutClicked}
        />
      </Menu>
    </MenuFlex>
  )
}

function Content({ points, pointsName, membershipName, membershipNumber, onLogoutClicked }) {
  const SIGN_OUT = 'Sign Out'
  return (
    <>
      {(membershipNumber || points) && (
        <Flex flexDirection='column' m={1}>
          {membershipNumber && (
            <Flex>
              <Text fontSize={1} bold mr={1}>
                {membershipName}:
              </Text>
              <Text fontSize={1}>{membershipNumber}</Text>
            </Flex>
          )}
          {points && (
            <Flex>
              <Text fontSize={1} bold mr={1}>
                {pointsName}:
              </Text>
              <Text fontSize={1}>{points}</Text>
            </Flex>
          )}
          <StyledDivider />
        </Flex>
      )}
      <SmallMenuItem label={SIGN_OUT} onClick={onLogoutClicked} mt={points ? 0 : undefined}>
        {SIGN_OUT}
      </SmallMenuItem>
    </>
  )
}

Content.propTypes = {
  points: PropTypes.string,
  pointsName: PropTypes.string,
  membershipName: PropTypes.string,
  membershipNumber: PropTypes.string,
  onLogoutClicked: PropTypes.func,
}

SignedIn.displayName = 'SignedIn'

SignedIn.propTypes = {
  className: PropTypes.string,
  color: PropTypes.string,
  profile: PropTypes.object,
  pointsName: PropTypes.string,
  membershipName: PropTypes.string,
  onLogoutClicked: PropTypes.func,
}

export default memo(SignedIn)
